import {
  Asset,
  FilterData,
  FilterMetadata,
  FolderContent,
  GeometricSearchMatch,
  PartSearchMatch,
  VisualSearchMatch,
} from '@/api/generatedApi';
import { StyledListItem } from '@/components/AssetsPage/PopoverList';
import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { CopyMenuItem, getCopyValueFromColumn } from '@/components/ContextMenu/CopyMenuItem';
import { OpenInNewTabMenuItem } from '@/components/ContextMenu/OpenInNewTabMenuItem';
import { BodyText } from '@/components/BodyText';
import { BulkActionsToolbar } from '@/components/ContentsTable/BulkActionsToolbar';
import { BulkSelectionProvider } from '@/components/ContentsTable/BulkSelectionContext';
import { DataGridLazy } from '@/components/DataGrid/DataGridLazy';
import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useContextMenu } from '@/hooks/useContextMenu';
import { useDownloadAsset } from '@/hooks/useDownloadAsset';
import { useGlobalDeleteState } from '@/hooks/useGlobalDeleteState';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useSearchTabs } from '@/hooks/useSearchTabs';
import { useSearchTabCounts } from '@/hooks/useSearchTabCounts';
import { SearchPageObjectModel } from '@/poms/searchPageObjectModel';
import { createUseStyles } from '@/services/createUseStyles';
import { useTenants } from '@/services/tenants/useTenants';
import { ApiErrorDisplay } from '@/types/apiError';
import { insertMetadataColumnsAfterDetails } from '@/utils/metadata';
import { Download as DownloadIcon, FilterList, ViewColumn } from '@mui/icons-material';
import { Box, IconButton, ListItemIcon, ListItemText, Skeleton, Tooltip, useTheme } from '@mui/material';
import { SearchFilterPanel } from './SearchFilterPanel';
import {
  FooterPropsOverrides,
  GridFilterModel,
  GridLogicOperator,
  GridPreferencePanelsValue,
  GridToolbarProps,
  useGridApiContext,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { useMemo, useState, useEffect, useRef } from 'react';
import { serializeSearchFilterModel, selectedFiltersToGridFilterModel } from './searchFilterSerializer';
import { useMetadataColumnVisibility } from '@/hooks/useMetadataColumnVisibility';
import { useScrollPositionRestore } from '@/hooks/useScrollPositionRestore';
import { useNavigationType } from 'react-router-dom';
import { SearchTabs } from '../SearchTabs';
import { SearchTabCounts, SearchType } from '../types';
import { getColumns } from './columnDefinitions';
import { useCompareCart } from '@/services/compareCart';
import { SearchResultsDownload } from '../SearchResultsDownload';
import { NoSearchResults } from './NoSearchResults';

const ColumnsButton = () => {
  const apiReference = useGridApiContext();
  const theme = useTheme();

  const handleClick = () => {
    apiReference.current.showPreferences(GridPreferencePanelsValue.columns);
  };

  return (
    <Tooltip title='Columns' placement='bottom'>
      <IconButton
        size='small'
        onClick={handleClick}
        sx={{
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <ViewColumn fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const FiltersButton = () => {
  const apiReference = useGridApiContext();
  const theme = useTheme();

  const handleClick = () => {
    apiReference.current.showFilterPanel();
  };

  return (
    <Tooltip title='Filters' placement='bottom'>
      <IconButton
        size='small'
        onClick={handleClick}
        sx={{
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <FilterList fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const SearchTabsToolbar = ({
  shouldShowSearchTabs,
  toolbarActions,
  resultCounts,
  loadingStates,
}: GridToolbarProps & {
  shouldShowSearchTabs?: boolean;
  toolbarActions?: React.ReactNode;
  resultCounts?: SearchTabCounts;
  loadingStates?: Record<string, boolean>;
}) => (
  <Box
    sx={{
      padding: '8px 10px 6px 8px',
      flexWrap: 'nowrap',
      gap: '6px',
      minWidth: 0,
      overflow: 'visible',
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <Box
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        marginLeft: '8px',
      }}
    >
      {shouldShowSearchTabs && <SearchTabs resultCounts={resultCounts} loadingStates={loadingStates} />}
    </Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexShrink: 0,
      }}
    >
      <FiltersButton />
      <ColumnsButton />
      {toolbarActions}
      <BulkActionsToolbar />
    </Box>
  </Box>
);

const SearchTableFooter = ({ resultCount }: { resultCount?: number }) => {
  return (
    <Box
      sx={{
        borderTop: 1,
        borderColor: 'divider',
        minHeight: '36px',
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px',
        flexDirection: 'row',
        gap: '6px',
      }}
    >
      <BodyText>Results: {resultCount ?? ''}</BodyText>
      {resultCount === undefined && <Skeleton width={30} height={20} />}
    </Box>
  );
};

const createSearchTableFooter = (totalResultCount?: number) => {
  const FooterComponent = (properties: FooterPropsOverrides) => (
    <SearchTableFooter resultCount={totalResultCount} {...properties} />
  );
  FooterComponent.displayName = 'SearchTableFooterComponent';
  return FooterComponent;
};

const useStyles = createUseStyles({
  DataGrid: {
    borderRadius: '6px !important',
    '& .MuiDataGrid-cell': {
      lineHeight: 'unset !important',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    '& .MuiDataGrid-cellCheckbox': {
      cursor: 'default',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none !important',
    },
  },
  tableContainer: {
    display: 'flex',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  dataGridWrapper: {
    flex: 1,
    minWidth: 0,
    height: '100%',
  },
  errorContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: '24px',
  },
});

type MatchResponse = VisualSearchMatch | PartSearchMatch | GeometricSearchMatch;

export type MatchAsset = Asset & {
  contentType: 'asset';
  matchPercentage?: number;
  forwardMatchPercentage?: number;
  reverseMatchPercentage?: number;
};

export const SearchTable = ({
  fetchMatches,
  isLoading,
  error,
  shouldShowSearchTabs,
  bulkSelectionKey,
}: {
  fetchMatches: (parameters: { page: number; perPage: number }) => Promise<{
    matches: MatchResponse[];
    pageData: { total: number };
    filterData?: Partial<FilterData>;
  }>;
  isLoading: boolean;
  error?: ApiErrorDisplay;
  shouldShowSearchTabs: boolean;
  bulkSelectionKey?: string;
}) => {
  const c = useStyles();
  const { searchType, assetId, selectedFilters, searchQuery, setSelectedFilters } = useSearchFilters();
  const { asset: searchAsset } = useGetAsset({ assetId });
  const { permissions, currentUserIsAtleastTenantViewer } = useGetCurrentUser();
  const { isAnyDeletePending } = useGlobalDeleteState();
  const gridApi = useGridApiRef();
  const { getTenantId } = useTenants();
  const { selectedSearchTab } = useSearchTabs();
  const { counts: searchTabCounts, loadingStates: searchTabLoadingStates } = useSearchTabCounts();
  const { selectedForCompare, setSelectedForCompare } = useCompareCart();
  const [filterDataMetadata, setFilterDataMetadata] = useState<FilterMetadata | undefined>();
  const [filterDataExtensions, setFilterDataExtensions] = useState<Array<{ filter: string; count: number }>>([]);
  const [filterDataFolders, setFilterDataFolders] = useState<Array<{ filter: string; count: number }>>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

  const shouldTriggerSearchImmediatelyReference = useRef(false);
  const searchDebounceTimerReference = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousFilterModelStringReference = useRef<string>('');

  const triggerApply = () => {
    shouldTriggerSearchImmediatelyReference.current = true;
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);

    if (searchDebounceTimerReference.current) {
      clearTimeout(searchDebounceTimerReference.current);
    }

    if (shouldTriggerSearchImmediatelyReference.current) {
      shouldTriggerSearchImmediatelyReference.current = false;
      const { filters } = serializeSearchFilterModel(newFilterModel);
      setSelectedFilters(filters);
    } else {
      searchDebounceTimerReference.current = setTimeout(() => {
        const { filters } = serializeSearchFilterModel(newFilterModel);
        setSelectedFilters(filters);
      }, 500);
    }
  };

  useEffect(() => {
    return () => {
      if (searchDebounceTimerReference.current) {
        clearTimeout(searchDebounceTimerReference.current);
      }
    };
  }, []);

  const contextKey = `${assetId || ''}-${searchType || ''}-${JSON.stringify(selectedFilters)}-${searchQuery || ''}`;

  const navigationType = useNavigationType();
  useScrollPositionRestore({
    apiReference: gridApi,
    folderKey: `search:${assetId || ''}-${searchType || ''}-${searchQuery || ''}`,
    shouldRestore: navigationType === 'POP',
  });

  const metadataFieldNames = Object.keys(filterDataMetadata ?? {});

  const { metadataColumns, columnVisibilityModel, onColumnVisibilityModelChange, isMetadataLoading } =
    useMetadataColumnVisibility({
      metadataFieldNames,
      isLoadingFieldNames: false,
      canEditMetadata: permissions.canModifyAssetMetadata,
      contextKey,
      filterable: true,
      filterDataMetadata,
      onApplyFilter: triggerApply,
    });

  useEffect(() => {
    const availableColumns = new Set(['extension', 'folder', 'createdAt', 'creator']);
    for (const col of metadataColumns) {
      availableColumns.add(col.field);
    }

    const hasUrlFilters =
      selectedFilters.extensions.length > 0 ||
      selectedFilters.folders.length > 0 ||
      Object.keys(selectedFilters.metadata).length > 0;

    if (!hasInitializedFilters || (hasUrlFilters && metadataColumns.length > 0)) {
      const newFilterModel = selectedFiltersToGridFilterModel(selectedFilters, availableColumns);
      const newModelString = JSON.stringify(newFilterModel);
      if (previousFilterModelStringReference.current !== newModelString) {
        setFilterModel(newFilterModel);
        previousFilterModelStringReference.current = newModelString;
      }
      setHasInitializedFilters(true);
    }
  }, [selectedFilters, metadataColumns.length, hasInitializedFilters]);

  const transformedFetchMatches = async (parameters: { page: number; perPage: number }) => {
    const response = await fetchMatches(parameters);
    setTotalResultCount(response.pageData.total);

    if (parameters.page === 1) {
      if (response.filterData?.metadata) {
        setFilterDataMetadata(response.filterData.metadata);
      }
      if (response.filterData?.extensions) {
        setFilterDataExtensions(response.filterData.extensions);
      }
      if (response.filterData?.folders) {
        setFilterDataFolders(response.filterData.folders);
      }
    }

    const matchAssets: MatchAsset[] = response.matches.map(match => {
      const matchAsset: MatchAsset = {
        ...match.asset,
        id: match.asset.id,
        contentType: 'asset',
      };

      if ('matchPercentage' in match) {
        matchAsset.matchPercentage = match.matchPercentage;
      }

      if ('forwardMatchPercentage' in match && 'reverseMatchPercentage' in match) {
        matchAsset.forwardMatchPercentage = match.forwardMatchPercentage;
        matchAsset.reverseMatchPercentage = match.reverseMatchPercentage;
      }

      return matchAsset;
    });

    if (assetId && searchType && selectedForCompare.asset) {
      const selectedAsset = selectedForCompare.asset;
      const needsUpdate =
        selectedAsset.id === matchAssets[0]?.id &&
        (!selectedAsset.matchData ||
          selectedAsset.matchData.searchAssetId !== assetId ||
          selectedAsset.matchData.searchType !== searchType ||
          selectedAsset.matchData.matchPercentage !== matchAssets[0]?.matchPercentage ||
          selectedAsset.matchData.forwardMatchPercentage !== matchAssets[0]?.forwardMatchPercentage ||
          selectedAsset.matchData.reverseMatchPercentage !== matchAssets[0]?.reverseMatchPercentage);

      if (needsUpdate && matchAssets[0]) {
        setSelectedForCompare({
          asset: {
            ...selectedAsset,
            matchData: {
              searchAssetId: assetId,
              searchType,
              matchPercentage: matchAssets[0].matchPercentage,
              forwardMatchPercentage: matchAssets[0].forwardMatchPercentage,
              reverseMatchPercentage: matchAssets[0].reverseMatchPercentage,
            },
          },
        });
      }
    }

    return {
      matches: matchAssets,
      pageData: response.pageData,
    };
  };

  const { selectedRows, isLoadingAll, handleSelectionChange, clearSelection } = useBulkSelection<
    MatchAsset,
    { matches: MatchAsset[] }
  >({
    fetchContents: transformedFetchMatches,
    dataKey: 'matches',
    gridApi,
  });

  const [totalResultCount, setTotalResultCount] = useState<number | undefined>();

  const canUseBulkActions = [permissions.canDeleteAssets].some(Boolean);
  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<MatchAsset>({
    canUseContextMenu: true,
    getRowById: (rowId: string | number) => gridApi.current?.getRow<MatchAsset>(rowId) ?? undefined,
  });
  const { downloadAsset } = useDownloadAsset();

  const handleDownloadClick = async () => {
    if (!contextMenuClickedItem) return;
    handleCloseContextMenu();
    await downloadAsset(contextMenuClickedItem);
  };

  const extensionOptions = filterDataExtensions.map(extension => ({ value: extension.filter, count: extension.count }));
  const folderOptions = filterDataFolders.map(folder => ({ value: folder.filter, count: folder.count }));

  const columns = useMemo(() => {
    const baseColumns = getColumns({
      searchType,
      canOnlySearch: !currentUserIsAtleastTenantViewer(selectedSearchTab?.tenantId),
      isCrossTenant: selectedSearchTab?.tenantId !== getTenantId(),
      searchAsset,
      extensionOptions,
      folderOptions,
      onApplyFilter: triggerApply,
    });
    return insertMetadataColumnsAfterDetails(baseColumns, metadataColumns);
  }, [
    searchType,
    searchAsset,
    metadataColumns,
    selectedSearchTab,
    currentUserIsAtleastTenantViewer,
    getTenantId,
    extensionOptions,
    folderOptions,
    triggerApply,
  ]);

  const previousFiltersReference = useRef<string | undefined>(undefined);
  const previousSearchQueryReference = useRef<string | undefined>(undefined);
  const previousBulkSelectionKeyReference = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentFiltersKey = JSON.stringify(selectedFilters);
    const filtersChanged = previousFiltersReference.current !== currentFiltersKey;
    const searchQueryChanged = previousSearchQueryReference.current !== searchQuery;
    const bulkSelectionKeyChanged = previousBulkSelectionKeyReference.current !== bulkSelectionKey;

    if (filtersChanged || searchQueryChanged || bulkSelectionKeyChanged) {
      clearSelection();
      previousFiltersReference.current = currentFiltersKey;
      previousSearchQueryReference.current = searchQuery;
      previousBulkSelectionKeyReference.current = bulkSelectionKey;
    }
  }, [selectedFilters, searchQuery, bulkSelectionKey]);

  const fetchAllResults = async (): Promise<MatchAsset[]> => {
    if (!totalResultCount) {
      return [];
    }

    const allMatches: MatchAsset[] = [];
    const totalPages = Math.ceil(totalResultCount / DATA_GRID_LAZY_PAGE_SIZE);

    for (let page = 1; page <= totalPages; page += 1) {
      const response = await transformedFetchMatches({
        page,
        perPage: DATA_GRID_LAZY_PAGE_SIZE,
      });
      allMatches.push(...response.matches);
    }

    return allMatches;
  };

  return (
    <Box className={c.tableContainer}>
      <Box
        className={c.dataGridWrapper}
        data-testid={SearchPageObjectModel.DataGridWrapper}
        onContextMenu={handleGridContextMenu}
      >
        <BulkSelectionProvider
          value={{
            selectedRows,
            isLoadingAll,
            clearSelection,
            selectionCount: selectedRows.length,
          }}
        >
          <DataGridLazy<MatchAsset, { matches: MatchAsset[] }>
            apiRef={gridApi}
            className={c.DataGrid}
            columns={columns}
            sx={{
              '& .MuiBadge-root .MuiBadge-badge': { display: 'none' },
            }}
            initialState={{
              pinnedColumns: {
                left: ['name'],
                right: ['__check__'],
              },
            }}
            filterModel={filterModel}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            onFilterModelChange={handleFilterModelChange}
            fetchRows={transformedFetchMatches}
            dataKey='matches'
            rowHeight={80}
            columnHeaderHeight={40}
            loading={isLoading || isMetadataLoading || isAnyDeletePending}
            checkboxSelection={canUseBulkActions}
            onRowSelectionModelChange={handleSelectionChange}
            error={error}
            density='compact'
            disableRowSelectionOnClick
            showToolbar
            slots={{
              toolbar: SearchTabsToolbar,
              footer: createSearchTableFooter(totalResultCount),
              noRowsOverlay: NoSearchResults,
              filterPanel: SearchFilterPanel,
            }}
            slotProps={{
              toolbar: {
                shouldShowSearchTabs,
                resultCounts: searchTabCounts,
                loadingStates: searchTabLoadingStates,
                toolbarActions: (
                  <SearchResultsDownload
                    fetchAllResults={fetchAllResults}
                    disabled={!totalResultCount}
                    searchAssetPath={searchAsset?.path}
                    searchAssetId={searchAsset?.id}
                    searchQuery={searchQuery ?? undefined}
                    searchType={searchType as SearchType | undefined}
                  />
                ),
              } as {
                shouldShowSearchTabs?: boolean;
                toolbarActions?: React.ReactNode;
                resultCounts?: SearchTabCounts;
                loadingStates?: Record<string, boolean>;
              },
              filterPanel: {
                logicOperators: [GridLogicOperator.And],
                getColumnForNewFilter: ({ currentFilters, columns }) => {
                  const fieldsWithIsAnyOf = new Set(
                    currentFilters?.filter(f => f.operator === 'isAnyOf').map(f => f.field) ?? []
                  );

                  const availableColumn = columns.find(col => {
                    if (!col.filterable) return false;
                    if (fieldsWithIsAnyOf.has(col.field)) return false;
                    return true;
                  });

                  // eslint-disable-next-line unicorn/no-null
                  return availableColumn?.field ?? null;
                },
                filterFormProps: {
                  columnInputProps: {
                    sx: { minWidth: 150 },
                  },
                  filterColumns: ({ field, columns, currentFilters }) => {
                    const fieldsWithIsAnyOf = new Set(
                      currentFilters?.filter(f => f.operator === 'isAnyOf').map(f => f.field) ?? []
                    );

                    return columns
                      .filter(col => {
                        if (col.field === field) return true;
                        if (fieldsWithIsAnyOf.has(col.field)) return false;
                        return true;
                      })
                      .map(col => col.field);
                  },
                },
              },
            }}
            hideFooter={totalResultCount === 0}
          />
        </BulkSelectionProvider>
      </Box>
      {(contextMenuClickedItem || getCopyValueFromColumn(columns, contextMenuClickedItem, contextMenuClickedField)) && (
        <BaseContextMenu anchorPosition={contextMenuPosition} onClose={handleCloseContextMenu}>
          {contextMenuClickedItem && contextMenuClickedField === 'name' && (
            <OpenInNewTabMenuItem item={contextMenuClickedItem} onClose={handleCloseContextMenu} />
          )}
          {contextMenuClickedItem && contextMenuClickedField === 'folder' && contextMenuClickedItem.folderId && (
            <OpenInNewTabMenuItem
              item={{ id: contextMenuClickedItem.folderId, contentType: 'folder' } as FolderContent}
              onClose={handleCloseContextMenu}
            />
          )}
          <CopyMenuItem
            columns={columns}
            row={contextMenuClickedItem}
            field={contextMenuClickedField}
            onCopy={handleCloseContextMenu}
          />
          {contextMenuClickedItem && (
            <StyledListItem onClick={() => void handleDownloadClick()}>
              <ListItemIcon>
                <DownloadIcon sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <ListItemText>Download</ListItemText>
            </StyledListItem>
          )}
        </BaseContextMenu>
      )}
    </Box>
  );
};
